import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useUserProfile } from "@/hooks/use-user-profile";
import { useUserRole } from "@/hooks/use-user-role";
import NoOrganizationGuard from "@/components/admin/NoOrganizationGuard";
import LoadingFallback from "@/components/LoadingFallback";
import { useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import AdminSidebar from "@/components/admin/AdminSidebar";
import { GlobalSearch } from "@/components/admin/GlobalSearch";
import { ThemeToggle } from "./ThemeToggle";
import { AdminViewProvider } from "@/context/AdminViewContext";
import { AdminViewSwitcher } from "@/components/admin/AdminViewSwitcher";

const AdminLayout: React.FC = () => {
  const { logout, user } = useAuth();
  const { profile, isLoading: profileLoading, error: profileError } = useUserProfile();
  const { data: userRole } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  // Pages that don't require organization membership
  const publicPages = ['/admin/billing', '/admin/site-settings'];
  const isPublicPage = publicPages.some(page => location.pathname.startsWith(page));

  // Debug logging for organization detection (development only)
  if (import.meta.env.DEV) {
    console.log('AdminLayout Debug:', {
      user: user ? { id: user.id, email: user.email } : null,
      profile: profile ? { 
        id: (profile as any).id, 
        organization_id: (profile as any).organization_id, 
        role: userRole,
        name: (profile as any).name 
      } : null,
      profileLoading,
      profileError: profileError?.message,
      currentPath: location.pathname,
      isPublicPage
    });
  }

  if (profileLoading) {
    return <LoadingFallback />;
  }

  // If user has no organization and trying to access restricted pages
  if (!(profile as any)?.organization_id && !isPublicPage) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AdminSidebar />
          <div className="flex flex-col flex-1 min-h-screen">
          <header className="flex items-center gap-3 border-b bg-background/50 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-3 sm:p-4">
            <SidebarTrigger className="lg:hidden" />
            <div className="flex-1 max-w-md">
              <GlobalSearch />
            </div>
            <div className="ml-auto">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={async () => {
                  try {
                    await logout();
                    navigate("/");
                  } catch (error) {
                    console.error('Logout failed:', error);
                    navigate("/");
                  }
                }}
                className="text-xs sm:text-sm"
              >
                Logout
              </Button>
            </div>
          </header>
          <main className="flex-1 p-3 sm:p-4 w-full max-w-screen-2xl overflow-auto">
              <NoOrganizationGuard />
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <AdminViewProvider>
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AdminSidebar />
          <div className="flex flex-col flex-1 min-h-screen">
            <header className="flex items-center gap-3 border-b bg-background/50 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-3 sm:p-4">
              <SidebarTrigger className="lg:hidden" />
              <div className="flex-1 max-w-md">
                <GlobalSearch />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <AdminViewSwitcher />
                <ThemeToggle />
                <Button
                variant="outline" 
                size="sm" 
                onClick={async () => {
                  try {
                    await logout();
                    navigate("/");
                  } catch (error) {
                    console.error('Logout failed:', error);
                    // Force navigation even if logout fails to prevent being stuck
                    navigate("/");
                  }
                }}
                className="text-xs sm:text-sm"
              >
                Logout
              </Button>
            </div>
          </header>
          <main className="flex-1 p-3 sm:p-4 w-full max-w-screen-2xl overflow-auto">
            <Outlet />
          </main>
          <footer className="mt-auto border-t bg-background/50 p-2 sm:p-3">
            <div className="w-full text-center">
              <p className="text-xs text-muted-foreground">
                Need help? Contact <a href="mailto:support@zoocertica.com" className="text-primary hover:underline">support@zoocertica.com</a>
              </p>
            </div>
            </footer>
          </div>
        </div>
      </SidebarProvider>
    </AdminViewProvider>
  );
};

export default AdminLayout;
